/**
 * Admin Route Component
 *
 * Wraps routes that require admin role.
 * Redirects to login if not authenticated, to home if not admin.
 *
 * Modeled on ProtectedRoute.tsx with an additional role check.
 * This is Layer 2 of three-layer security — see architecture/dashboard-structure.md.
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../core/auth";
import "../styles/palette.css";

interface AdminRouteProps {
  children: React.ReactNode;
}

export function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated, loading, profile, isConfigured } = useAuth();
  const location = useLocation();

  // DEV mode: skip auth unless VITE_FORCE_AUTH is set
  if (import.meta.env.DEV && import.meta.env.VITE_FORCE_AUTH !== "true") {
    return <>{children}</>;
  }

  // Show nothing while loading auth state
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          backgroundColor: "var(--bg-page)",
          color: "var(--text-secondary)",
          fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
        }}
      >
        Loading...
      </div>
    );
  }

  // If Supabase is not configured, allow access (dev without credentials)
  if (!isConfigured) {
    console.warn(
      "Supabase not configured - allowing admin access without auth",
    );
    return <>{children}</>;
  }

  // Not authenticated — redirect to login
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Authenticated but not admin — redirect to home
  if (profile?.role !== "admin") {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export default AdminRoute;
